import PropTypes from 'prop-types';
import classNames from 'classnames';
import React, { useCallback, useState, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import Popper from '@material-ui/core/Popper';
import { makeStyles } from '@material-ui/core/styles';
import { deepPure } from '~/util';
import User from '~/modules/common/components/User';
import ChangeAllocationUserAsOfDateDialog from '~/modules/resourcing/common/components/ChangeAllocationUserAsOfDateDialog';
import UserAvailabilityDropdown from '~/modules/common/components/UserAvailabilityDropdown';
import SearchableRoleDropDown from '~/modules/resourcing/common/components/SearchableRoleDropDown';
import { useResourceRequestToolbarContext } from '~/modules/projects/resourcing-plan/hooks';
import { RequestActions } from '~/modules/resourcing/common/components/RequestActions/RequestActions';
import { useProjectContext } from '~/modules/resourcing/common/contexts';
import useMeContext from '~/modules/me/useMeContext';
import { useProjectResourcePlanPermissions } from '~/modules/resourcing/hooks';
import { ReleaseResourceDialog } from '~/modules/resourcing/common/components/ReleaseResourceRequestDialog';
import { useDialogState } from '~/modules/common/hooks';
import { dateToMidnightUTCString } from '~/modules/common/dates/convert';
import useStyles from './useStyles';

const useInputStyles = makeStyles(theme => ({
  input: {
    ...theme.typography.body2
  }
}));

const popperStyle = { width: 'fit-content' };

const FullWidthPopper = props => {
  return <Popper {...props} style={popperStyle} placement="bottom-start" />;
};

export const validateRole = role => (role && !role.isCreatable) || !role;

export const ResourceAllocationChartLeftItem = ({
  qeIdTag,
  resourceAllocation,
  isEditEnabled,
  handleRoleChange,
  handleUserChange,
  handleResourceChangeAndUpdateResourceAllocation,
  resourceRequest,
  actions,
  releaseResourceAllocation,
  releaseResourceAllocationsForRequest,
  showReleaseResourcesDialog,
  closeReleaseResourcesDialog
}) => {
  const classes = useStyles();
  const inputClasses = useInputStyles();
  const intl = useIntl();
  const { project } = useProjectContext();

  const { permissionsMap } = useMeContext();

  const [newResourceUser, setNewResourceUser] = useState(null);
  const { dateRange: chartDateRange } = useResourceRequestToolbarContext();
  const {
    open: isDialogOpen,
    openDialog: onDialogOpen,
    closeDialog: onDialogClose
  } = useDialogState();

  const onUserChangeConfirm = useCallback(
    ({ asOfDate, impactedTasks }) => {
      if (asOfDate) {
        handleResourceChangeAndUpdateResourceAllocation({
          resourceAllocation,
          asOfDate,
          project,
          newResourceUser,
          impactedTasks,
          onDialogClose
        });
      } else {
        handleUserChange(newResourceUser, impactedTasks);
        onDialogClose();
      }
    },
    [
      handleResourceChangeAndUpdateResourceAllocation,
      resourceAllocation,
      project,
      onDialogClose,
      newResourceUser,
      handleUserChange
    ]
  );

  const onRoleChange = useCallback(
    role => {
      if (validateRole(role)) {
        handleRoleChange({ resourceAllocation, role });
      }
    },
    [handleRoleChange, resourceAllocation]
  );

  const onUserChange = useCallback(
    user => {
      if (!user) return;
      if (user && resourceAllocation.user) {
        setNewResourceUser(user);
        onDialogOpen();
      } else {
        handleUserChange(user);
      }
    },
    [resourceAllocation.user, onDialogOpen, handleUserChange]
  );

  const {
    isViewAdvancedSettingsEnabled,
    isViewAvailabilitySettingsEnabled
  } = useProjectResourcePlanPermissions(permissionsMap);

  const showTimeOff = Boolean(
    isViewAvailabilitySettingsEnabled && Boolean(chartDateRange)
  );

  const showHolidays = isViewAdvancedSettingsEnabled && Boolean(chartDateRange);

  const formattedDateRange = useMemo(
    () =>
      chartDateRange
        ? {
            startDate: dateToMidnightUTCString(chartDateRange.startDate),
            endDate: dateToMidnightUTCString(chartDateRange.endDate)
          }
        : null,
    [chartDateRange]
  );

  return (
    <>
      <div className={classes.root}>
        <div className={classes.row} data-qe-id={qeIdTag}>
          <RequestActions
            qeIdTag={`RequestActions_${qeIdTag}`}
            actions={actions}
          />
          {showReleaseResourcesDialog && (
            <ReleaseResourceDialog
              resourceRequest={resourceRequest}
              onReleaseResourceAllocationsForRequest={releaseResourceAllocationsForRequest(
                {
                  resourceRequestId: resourceAllocation.resourceRequestId,
                  chartDateRange: formattedDateRange,
                  showTimeOff,
                  showHolidays
                }
              )}
              allocation={resourceAllocation}
              projectUri={project.id}
              onReleaseResourceAllocation={releaseResourceAllocation}
              open={showReleaseResourcesDialog}
              onCancelClick={closeReleaseResourcesDialog}
              canEditTask={project.permittedActionUris.includes(
                'urn:replicon:project-action:edit-tasks'
              )}
              chartDisplayDateRange={chartDateRange}
            />
          )}
          {isEditEnabled ? (
            <>
              <div
                className={classNames(
                  classes.roleRowCell,
                  classes.roleRowCellEdit
                )}
              >
                <SearchableRoleDropDown
                  dataQeId={`RoleDropDown_${qeIdTag}`}
                  value={resourceAllocation.role}
                  placeholder={intl.formatMessage({
                    id: 'resourceRequestTitle.anyRole'
                  })}
                  classes={inputClasses}
                  variant="standard"
                  onChange={onRoleChange}
                  canUserEditSkillAssignment
                  multiline
                  ariaLabel={intl.formatMessage({
                    id: 'rolesDropdown.selectRole'
                  })}
                  customPopperComponent={FullWidthPopper}
                />
              </div>
              <div
                className={classNames(classes.allocationRowCell, classes.user)}
              >
                <UserAvailabilityDropdown
                  dataQeId={`UserAvailabilityDropdown_${qeIdTag}`}
                  dateRange={chartDateRange}
                  onChange={onUserChange}
                  value={
                    resourceAllocation.user && resourceAllocation.user.user
                  }
                  userRole={resourceAllocation.role}
                  placeholder={intl.formatMessage({
                    id: 'addResource.selectResource'
                  })}
                  optionTypeText={intl.formatMessage({
                    id: 'moreOptions.users'
                  })}
                />
              </div>
            </>
          ) : (
            <>
              <div
                className={classNames(
                  classes.roleRowCell,
                  classes.roleRowCellReadonly
                )}
              >
                <span
                  className={classNames(classes.text, classes.roleTextReadonly)}
                >
                  {resourceAllocation.role &&
                  resourceAllocation.role.displayText ? (
                    resourceAllocation.role.displayText
                  ) : (
                    <FormattedMessage id="resourceRequestReadonlyItem.noRole" />
                  )}
                </span>
              </div>
              <div
                className={classNames(
                  classes.allocationRowCell,
                  classes.allocationRowCellReadonly,
                  classes.userReadonly
                )}
              >
                <span className={classNames(classes.text, classes.userText)}>
                  <User
                    dataQeId={`User_${qeIdTag}`}
                    user={
                      resourceAllocation.user && resourceAllocation.user.user
                    }
                  />
                </span>
              </div>
            </>
          )}
        </div>
      </div>
      {isDialogOpen && (
        <ChangeAllocationUserAsOfDateDialog
          resourceAllocation={resourceAllocation}
          open={isDialogOpen}
          onClose={onDialogClose}
          onConfirm={onUserChangeConfirm}
          permissions={project.permittedActionUris}
          newResourceUser={newResourceUser}
        />
      )}
    </>
  );
};

ResourceAllocationChartLeftItem.propTypes = {
  qeIdTag: PropTypes.string,
  resourceAllocation: PropTypes.object.isRequired,
  isEditEnabled: PropTypes.bool.isRequired,
  handleUserChange: PropTypes.func.isRequired,
  handleRoleChange: PropTypes.func.isRequired,
  handleResourceChangeAndUpdateResourceAllocation: PropTypes.func.isRequired,
  resourceRequest: PropTypes.object,
  actions: PropTypes.array,
  releaseResourceAllocation: PropTypes.func,
  releaseResourceAllocationsForRequest: PropTypes.func,
  showReleaseResourcesDialog: PropTypes.bool,
  closeReleaseResourcesDialog: PropTypes.func
};

export default deepPure(ResourceAllocationChartLeftItem);
